import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { simulationV2 } from '~/services/simulation-service';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useStoreActions, useStoreState } from '~/store';
import { useSession } from '~/hooks/useSession';
import type { Simulation, SimulationServiceVersion } from '~/store/simulation/types';

// v2's GET /simulation has no status filter; mirror the v1 gateway's whitelist.
const LANDING_STATUSES: ReadonlySet<string> = new Set(['processing', 'completed', 'configuring', 'exception']);

// Tag by source store (ground truth); the stamp routes downstream mutations.
const tagServiceVersion = (sim: Simulation, serviceVersion: SimulationServiceVersion): Simulation => ({
  ...sim,
  simulationSetupData: { ...(sim.simulationSetupData ?? {}), serviceVersion },
});

// Disjoint sources → concat (no dedup), newest-first; v2 rows get the status whitelist.
export function mergeSimulationLists(v1Data: Simulation[] | undefined, v2Data: Simulation[] | undefined): Simulation[] {
  const v1Rows = (v1Data ?? []).map((sim) => tagServiceVersion(sim, 'v1'));
  const v2Rows = (v2Data ?? [])
    .filter((sim) => LANDING_STATUSES.has(sim.simulationStatus))
    .map((sim) => tagServiceVersion(sim, 'v2'));
  return [...v1Rows, ...v2Rows].sort(
    (a, b) => new Date(b.creationTimestamp).getTime() - new Date(a.creationTimestamp).getTime(),
  );
}

/** Dual-source landing list: v1 summary thunk (store) + v2 GET /simulation (React Query), merged. */
export function useMergedSimulations(): { data: Simulation[]; isLoading: boolean; isError: boolean } {
  const { isShipiumView } = useSession();
  const v1Summaries = useStoreState((state) => state.simulation.simulationSummaries);
  const v1Loading = useStoreState((state) => state.loading.loadingFlags.simulation);
  const fetchSimulationSummaries = useStoreActions((actions) => actions.simulation.fetchSimulationSummaries);
  const dispatchMessage = useStoreActions((actions) => actions.messages.dispatchMessage);
  useEffect(() => {
    fetchSimulationSummaries({});
  }, [fetchSimulationSummaries, isShipiumView]);

  const v2QueryOptions = useQueryCreator(simulationV2.list, { args: [] });
  const v2Query = useQuery({
    ...v2QueryOptions,
    queryKey: [...v2QueryOptions.queryKey, String(isShipiumView)],
    // surface v2 read failures like the v1 thunk does
    onError: () => dispatchMessage({ noticeType: 'error', content: 'Error loading simulations. Please retry.' }),
  });

  const data = useMemo(
    () => mergeSimulationLists(Object.values(v1Summaries ?? {}), v2Query.data),
    [v1Summaries, v2Query.data],
  );

  return {
    data,
    isLoading: v1Loading || v2Query.isLoading,
    isError: v2Query.isError,
  };
}
